import React, { useEffect, useState } from 'react'
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Badge
} from 'reactstrap'
import { Button } from 'react-bootstrap'
import Select from 'react-select'
import { ObjetoImpCatalogo, c_TipoRelacion } from '../../../data/catalogs/CatalogosSAT'

const panelStyles = {
    main: {
        border: '1px solid #eceff4',
        borderRadius: '14px',
        padding: '22px 22px 8px',
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)',
        boxShadow: '0 8px 24px rgba(40, 55, 80, 0.06)'
    },
    advanced: {
        border: '1px solid #e8edf3',
        borderRadius: '12px',
        padding: '18px 18px 4px',
        background: '#f8fafc'
    },
    heading: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '12px'
    },
    title: {
        margin: 0,
        fontSize: '15px',
        fontWeight: 700,
        color: '#24324a'
    },
    subtitle: {
        display: 'block',
        marginTop: '4px',
        fontSize: '12px',
        color: '#728197'
    },
    toggleButton: {
        padding: 0,
        fontSize: '13px',
        fontWeight: 600
    },
    footerHint: {
        marginTop: '-2px',
        marginBottom: '16px',
        fontSize: '12px',
        color: '#728197'
    }
}

const OBJETO_IMP_OBJETO = '02'
const OBJETO_IMP_NO_OBJETO = '01'
const IVA_EXENTO_TIPO_FACTOR = 'Exento'

const hasAdvancedValues = (tempItem) => (
    Number(tempItem.retenciones) !== 0 ||
    tempItem.CuentaPredial !== '' ||
    tempItem.InformacionAduanera !== ''
)

const getSuggestedUseCases = (tempItem) => {
    const content = `${tempItem.descripcion || ''} ${tempItem.noIdentificacion || ''}`.toLowerCase()
    const suggestions = []

    if (content.includes('renta') || content.includes('arrend') || content.includes('alquiler')) {
        suggestions.push('Si este concepto corresponde a arrendamiento, revisa la cuenta predial en opciones avanzadas.')
    }

    if (content.includes('import') || content.includes('aduan') || content.includes('pedimento')) {
        suggestions.push('Si este concepto corresponde a importacion, revisa el pedimento en opciones avanzadas.')
    }

    return suggestions
}

const normalizeContent = (value = '') => (
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
)

const getRfcType = (rfc = '') => {
    const normalizedRfc = (rfc || '').trim().toUpperCase()

    if (normalizedRfc.length === 12) {
        return 'moral'
    }

    if (normalizedRfc.length === 13) {
        return 'fisica'
    }

    return 'unknown'
}

const getAdvancedSignals = (tempItem, issuerRfc, customerRfc) => {
    const content = normalizeContent(`${tempItem.descripcion || ''} ${tempItem.noIdentificacion || ''}`)
    const issuerType = getRfcType(issuerRfc)
    const customerType = getRfcType(customerRfc)

    const arrendamiento = ['renta', 'arrend', 'alquiler', 'inmueble', 'local'].some(keyword => content.includes(keyword))
    const importacion = ['import', 'aduan', 'pedimento', 'fraccion arancel', 'comercio exterior'].some(keyword => content.includes(keyword))
    const servicioConRetencion = ['honorario', 'servicio profesional', 'asesoria', 'consultoria', 'arrend'].some(keyword => content.includes(keyword))
    const retenciones = issuerType === 'fisica' && customerType === 'moral' && servicioConRetencion

    const messages = []

    if (arrendamiento) {
        messages.push('Detectamos un posible concepto de arrendamiento. Revisa cuenta predial en opciones avanzadas.')
    }

    if (importacion) {
        messages.push('Detectamos un posible concepto de importacion. Revisa no. pedimento en opciones avanzadas.')
    }

    if (retenciones) {
        messages.push('Este concepto podria requerir retenciones por el tipo de servicio y RFCs involucrados. Verifica impuestos retenidos.')
    }

    return {
        any: arrendamiento || importacion || retenciones,
        arrendamiento,
        importacion,
        retenciones,
        messages
    }
}

const isPedimentoComplete = (value = '') => /^\d{2} \d{2} \d{4} \d{7}$/.test(value)

const parsePedimento = (value = '') => {
    const [anioValidacion = '', aduana = '', patente = '', consecutivo = ''] = (value || '').split(' ')

    return {
        anioValidacion,
        aduana,
        patente,
        consecutivo
    }
}

const composePedimento = ({ anioValidacion, aduana, patente, consecutivo }) => {
    if (!anioValidacion && !aduana && !patente && !consecutivo) {
        return ''
    }

    return `${anioValidacion} ${aduana} ${patente} ${consecutivo}`.trim()
}

const LineItemForm = ({
    tempItem,
    setTempItem,
    claves,
    unidades,
    aduanas,
    patentes,
    impuestos,
    onOpenAddClaveModal,
    onOpenAddClaveUnidadModal,
    onOpenAddAduanaModal,
    onOpenAddPatenteModal,
    onSubmit,
    submitLabel = 'Agregar Concepto',
    formIdPrefix = 'line-item',
    showSubmitButton = true,
    layout = 'default',
    issuerRfc,
    customerRfc,
    // CFDI Relacionados (invoice-level, only passed from the main form, not the edit modal)
    cfdiRelacionados,
    tipoDeComprobante,
    showCfdiRelacionadosForm,
    onToggleCfdiRelacionadosForm,
    onOpenAddRelacionModal,
    onRemoveRelacion
}) => {
    const isModalLayout = layout === 'modal'
    const [showAdvanced, setShowAdvanced] = useState(hasAdvancedValues(tempItem))
    const advancedSignals = getAdvancedSignals(tempItem, issuerRfc, customerRfc)
    const [pedimentoParts, setPedimentoParts] = useState(parsePedimento(tempItem.InformacionAduanera))

    useEffect(() => {
        if (hasAdvancedValues(tempItem) || advancedSignals.any) {
            setShowAdvanced(true)
        }
    }, [tempItem, advancedSignals.any])

    useEffect(() => {
        const composedPedimento = composePedimento(pedimentoParts)

        if ((tempItem.InformacionAduanera || '') !== composedPedimento) {
            setPedimentoParts(parsePedimento(tempItem.InformacionAduanera))
        }
    }, [tempItem.InformacionAduanera])

    const columns = isModalLayout
        ? {
            description: { xs: 12, sm: 12, md: 12, lg: 12 },
            compact: { xs: 12, sm: 6, md: 6, lg: 6 },
            sat: { xs: 12, sm: 6, md: 6, lg: 6 },
            advancedHalf: { xs: 12, sm: 6, md: 6, lg: 6 },
            advancedQuarter: { xs: 12, sm: 6, md: 6, lg: 3 }
        }
        : {
            description: { xs: 12, sm: 12, md: 12, lg: 6 },
            compact: { xs: 12, sm: 6, md: 6, lg: 3 },
            sat: { xs: 12, sm: 6, md: 6, lg: 3 },
            advancedHalf: { xs: 12, sm: 6, md: 6, lg: 6 },
            advancedQuarter: { xs: 12, sm: 6, md: 6, lg: 3 }
        }

    const updateTempItem = (field, value) => {
        const nextItem = {
            ...tempItem,
            [field]: value
        }

        const retenciones = field === 'retenciones' ? value : nextItem.retenciones
        const objetoImp = field === 'ObjetoImp' ? value : nextItem.ObjetoImp
        const hasRetenciones = Number(retenciones) !== 0

        if (hasRetenciones && objetoImp?.value === OBJETO_IMP_NO_OBJETO) {
            const ivaExentoIndex = impuestos.trasladados.findIndex((option) => {
                return option.value?.some((tax) => tax.tipoFactor === IVA_EXENTO_TIPO_FACTOR)
            })

            nextItem.ObjetoImp = ObjetoImpCatalogo.find(o => o.value === OBJETO_IMP_OBJETO) || objetoImp
            nextItem.impuestosTrasladados = ivaExentoIndex >= 0 ? ivaExentoIndex : nextItem.impuestosTrasladados
        }

        setTempItem(nextItem)
    }

    const updatePedimentoPart = (field, value) => {
        const nextParts = {
            ...pedimentoParts,
            [field]: value
        }

        setPedimentoParts(nextParts)
        updateTempItem('InformacionAduanera', composePedimento(nextParts))
    }

    const toggleRequiredError = (field, isInvalid) => {
        const element = document.getElementById(`${formIdPrefix}-${field}`)

        if (!element) {
            return
        }

        if (isInvalid) {
            element.classList.add('required-error')
            return
        }

        element.classList.remove('required-error')
    }

    const suggestedUseCases = getSuggestedUseCases(tempItem)
    const helperMessages = advancedSignals.messages.length > 0 ? advancedSignals.messages : suggestedUseCases

    return (
        <>
            <div style={panelStyles.main}>
                <div style={panelStyles.heading}>
                    <div>
                        <h5 style={panelStyles.title}>Datos principales del concepto</h5>
                        <small style={panelStyles.subtitle}>
                            Captura primero la informacion comercial y fiscal.
                        </small>
                    </div>
                </div>

                <Row>
                    <Col {...columns.description}>
                        <FormGroup>
                            <Label>Descripción</Label>

                            <Input
                                id={`${formIdPrefix}-descripcion`}
                                type="text"
                                value={tempItem.descripcion}
                                onBlur={(e) => {
                                    toggleRequiredError('descripcion', e.target.value === '')
                                }}
                                onChange={(e) => {
                                    updateTempItem('descripcion', e.target.value)
                                }}
                            />
                        </FormGroup>
                    </Col>
                    <Col {...columns.compact}>
                        <FormGroup>
                            <Label>Cantidad</Label>

                            <Input
                                type="number"
                                id={`${formIdPrefix}-cantidad`}
                                placeholder="10"
                                onBlur={(e) => {
                                    toggleRequiredError('cantidad', e.target.value === '')
                                }}
                                onChange={(e) => {
                                    updateTempItem('cantidad', e.target.value)
                                }}
                                value={tempItem.cantidad}
                            />
                        </FormGroup>
                    </Col>
                    <Col {...columns.compact}>
                        <FormGroup>
                            <Label>Precio Unitario</Label>

                            <InputGroup>
                                <InputGroupAddon addonType="prepend">
                                    <InputGroupText>$</InputGroupText>
                                </InputGroupAddon>

                                <Input
                                    id={`${formIdPrefix}-valorUnitario`}
                                    type="number"
                                    value={tempItem.valorUnitario}
                                    placeholder="850.00"
                                    onBlur={(e) => {
                                        toggleRequiredError('valorUnitario', Number(e.target.value) < 1)
                                    }}
                                    onChange={(e) => {
                                        updateTempItem('valorUnitario', e.target.value)
                                    }}
                                />
                            </InputGroup>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col {...columns.compact}>
                        <FormGroup>
                            <Label>Descuento</Label>

                            <InputGroup>
                                <Input
                                    id={`${formIdPrefix}-descuento`}
                                    type="number"
                                    placeholder="25"
                                    value={tempItem.descuento}
                                    onChange={(e) => {
                                        updateTempItem('descuento', e.target.value)
                                    }}
                                />
                                <InputGroupAddon addonType="append">
                                    <InputGroupText>%</InputGroupText>
                                </InputGroupAddon>
                            </InputGroup>
                        </FormGroup>
                    </Col>
                    <Col {...columns.compact}>
                        <FormGroup>
                            <Label>No. Identificación</Label>

                            <Input
                                id={`${formIdPrefix}-noIdentificacion`}
                                value={tempItem.noIdentificacion}
                                onChange={(e) => {
                                    updateTempItem('noIdentificacion', e.target.value)
                                }}
                            />
                        </FormGroup>
                    </Col>
                    <Col {...columns.compact}>
                        <FormGroup>
                            <Label>Objeto de Impuesto</Label>

                            <Select
                                value={tempItem.ObjetoImp}
                                onChange={(value) => {
                                    updateTempItem('ObjetoImp', value)
                                }}
                                placeholder="Seleccionar opcion"
                                options={ObjetoImpCatalogo}
                                classNamePrefix="select2-selection"
                            />
                        </FormGroup>
                    </Col>
                    <Col {...columns.compact}>
                        <FormGroup>
                            <Label>Impuestos Trasladados</Label>

                            <select
                                className="custom-select"
                                value={tempItem.impuestosTrasladados}
                                onChange={(e) => {
                                    updateTempItem('impuestosTrasladados', e.target.value)
                                }}
                            >
                                {impuestos.trasladados.map((v, i) => (
                                    <option key={i} value={i}>{v.label}</option>
                                ))}
                            </select>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col {...columns.sat}>
                        <FormGroup>
                            <Label htmlFor={`${formIdPrefix}-claveProdServ`}>
                                Clave de Producto
                            </Label>

                            <div className="d-flex">
                                <div style={{ flex: 1 }}>
                                    <Select
                                        id={`${formIdPrefix}-claveProdServ`}
                                        value={tempItem.claveProdServ}
                                        placeholder="Buscar clave"
                                        onChange={(target) => {
                                            updateTempItem('claveProdServ', target)
                                        }}
                                        options={claves}
                                        classNamePrefix="select2-selection"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    color="link"
                                    size="sm"
                                    className="btn-small"
                                    onClick={onOpenAddClaveModal}
                                    style={{ marginLeft: '5px' }}
                                >
                                    +
                                </Button>
                            </div>
                        </FormGroup>
                    </Col>
                    <Col {...columns.sat}>
                        <FormGroup>
                            <Label htmlFor={`${formIdPrefix}-claveUnidad`}>
                                Clave de Unidad
                            </Label>

                            <div className="d-flex">
                                <div style={{ flex: 1 }}>
                                    <Select
                                        id={`${formIdPrefix}-claveUnidad`}
                                        value={tempItem.claveUnidad}
                                        placeholder="Buscar clave"
                                        onChange={(target) => {
                                            updateTempItem('claveUnidad', target)
                                        }}
                                        options={unidades}
                                        classNamePrefix="select2-selection"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    color="link"
                                    size="sm"
                                    className="btn-small"
                                    onClick={onOpenAddClaveUnidadModal}
                                    style={{ marginLeft: '5px' }}
                                >
                                    +
                                </Button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </div>

            <div style={{ marginTop: '18px' }}>
                <div style={panelStyles.heading}>
                    <div>
                        <h5 style={panelStyles.title}>Cumplimiento y casos especiales</h5>
                        <small style={panelStyles.subtitle}>
                            Usa esta sección solo cuando el concepto requiera retenciones, cuenta predial o pedimento.
                        </small>
                    </div>
                    <Button
                        type="button"
                        variant="link"
                        style={panelStyles.toggleButton}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                        {showAdvanced ? 'Ocultar opciones avanzadas' : advancedSignals.any ? 'Revisar opciones avanzadas' : 'Mostrar opciones avanzadas'}
                    </Button>
                </div>

                {helperMessages.map((message, index) => (
                    <small key={index} className="d-block" style={panelStyles.footerHint}>
                        {message}
                    </small>
                ))}
            </div>

            {/* ── CFDI Relacionados (invoice-level, rendered only when props are passed) ── */}
            {cfdiRelacionados !== undefined && (
                <div className="mt-3 mb-2">
                    {tipoDeComprobante === 'E' ? (
                        <span className="text-muted" style={{ fontSize: '0.85em' }}>
                            CFDI Relacionados <Badge color="warning" pill className="ml-1">Requerido</Badge>
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ fontSize: '0.9em' }}
                            onClick={onToggleCfdiRelacionadosForm}
                        >
                            {showCfdiRelacionadosForm ? '▲' : '▼'} CFDI Relacionados (opcional)
                        </button>
                    )}

                    {showCfdiRelacionadosForm && (
                        <div className="mt-2">
                            {cfdiRelacionados.length > 0 && (
                                <div className="mb-2">
                                    {cfdiRelacionados.map((rel, idx) => {
                                        const tipoLabel = c_TipoRelacion.find(t => t.value === rel.tipoRelacion)?.label || rel.tipoRelacion
                                        return (
                                            <div key={idx} className="d-flex align-items-center mb-1 p-2" style={{ background: '#f8f9fa', borderRadius: 4, fontSize: '0.8em' }}>
                                                <span className="mr-2">
                                                    <Badge color="info" pill>{rel.tipoRelacion}</Badge>
                                                </span>
                                                <span className="mr-2" style={{ fontFamily: 'monospace' }}>{rel.uuid}</span>
                                                <span className="text-muted mr-auto">{tipoLabel}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger ml-2"
                                                    onClick={() => onRemoveRelacion(idx)}
                                                    title="Eliminar relación"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                            <Button
                                type="button"
                                variant="outline-primary"
                                size="sm"
                                onClick={onOpenAddRelacionModal}
                            >
                                + Agregar CFDI relacionado
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {showAdvanced && (
                <div style={panelStyles.advanced}>
                    <Row>
                        <Col {...columns.advancedHalf}>
                            <FormGroup>
                                <Label>Impuestos Retenidos</Label>

                                <select
                                    className="custom-select"
                                    value={tempItem.retenciones}
                                    onChange={(e) => {
                                        updateTempItem('retenciones', e.target.value)
                                    }}
                                >
                                    {impuestos.retenciones.map((v, i) => (
                                        <option key={i} value={i}>{v.label}</option>
                                    ))}
                                </select>
                            </FormGroup>
                        </Col>
                        <Col {...columns.advancedHalf}>
                            <FormGroup>
                                <Label>Cuenta Predial</Label>

                                <Input
                                    id={`${formIdPrefix}-CuentaPredial`}
                                    type="text"
                                    pattern="[0-9]{1,150}"
                                    value={tempItem.CuentaPredial}
                                    onChange={(e) => {
                                        updateTempItem('CuentaPredial', e.target.value)
                                    }}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col {...columns.advancedQuarter}>
                            <FormGroup>
                                <Label>Año de Validación</Label>

                                <Input
                                    value={pedimentoParts.anioValidacion}
                                    maxLength={2}
                                    placeholder="26"
                                    onChange={(e) => {
                                        updatePedimentoPart('anioValidacion', e.target.value.replace(/\D/g, '').slice(0, 2))
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col {...columns.advancedQuarter}>
                            <FormGroup>
                                <Label>Aduana</Label>

                                <div className="d-flex">
                                    <div style={{ flex: 1 }}>
                                        <Select
                                            value={aduanas.find((item) => item.value === pedimentoParts.aduana) || null}
                                            placeholder="Selecciona una aduana"
                                            onChange={(value) => {
                                                updatePedimentoPart('aduana', value?.value || '')
                                            }}
                                            options={aduanas}
                                            classNamePrefix="select2-selection"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        color="link"
                                        size="sm"
                                        className="btn-small"
                                        onClick={onOpenAddAduanaModal}
                                        style={{ marginLeft: '5px' }}
                                    >
                                        +
                                    </Button>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col {...columns.advancedQuarter}>
                            <FormGroup>
                                <Label>Patente</Label>

                                <div className="d-flex">
                                    <div style={{ flex: 1 }}>
                                        <Select
                                            value={patentes.find((item) => item.value === pedimentoParts.patente) || null}
                                            placeholder="Selecciona una patente"
                                            onChange={(value) => {
                                                updatePedimentoPart('patente', value?.value || '')
                                            }}
                                            options={patentes}
                                            classNamePrefix="select2-selection"
                                        />
                                    </div>
                                    <Button
                                        type="button"
                                        color="link"
                                        size="sm"
                                        className="btn-small"
                                        onClick={onOpenAddPatenteModal}
                                        style={{ marginLeft: '5px' }}
                                    >
                                        +
                                    </Button>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col {...columns.advancedQuarter}>
                            <FormGroup>
                                <Label>No. Pedimento</Label>

                                <Input
                                    id={`${formIdPrefix}-InformacionAduanera`}
                                    type="text"
                                    placeholder="9012345"
                                    pattern="[0-9]{7}"
                                    value={pedimentoParts.consecutivo}
                                    onBlur={(e) => {
                                        const value = composePedimento({ ...pedimentoParts, consecutivo: e.target.value })
                                        toggleRequiredError('InformacionAduanera', value !== '' && !isPedimentoComplete(value))
                                    }}
                                    onChange={(e) => {
                                        updatePedimentoPart('consecutivo', e.target.value.replace(/\D/g, '').slice(0, 7))
                                    }}
                                />
                                <small className="d-block mt-2 text-muted">
                                    Se genera como: año de validación + aduana + patente + consecutivo de 7 digitos.
                                </small>
                                {tempItem.InformacionAduanera && (
                                    <small className="d-block mt-1 text-muted">
                                        Pedimento generado: {tempItem.InformacionAduanera}
                                    </small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            )}

            {showSubmitButton && (
                <Row style={{ marginTop: '18px' }}>
                    <Col sm={12} style={{ textAlign: 'right' }}>
                        <Button
                            data-testid={`${formIdPrefix}-submit`}
                            type="button"
                            onClick={onSubmit}
                            color="primary"
                            className="btn waves-effect waves-light mb-2 mr-2"
                        >
                            <i className="mdi mdi-plus"></i> {submitLabel}
                        </Button>
                    </Col>
                </Row>
            )}
        </>
    )
}

export default LineItemForm
